import { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { I18n } from '../utils/i18n';

const I18nContext = createContext({ t: (key) => key, lang: 'en', setLang: () => {} });

export const I18nProvider = ({ defaultLang = 'en', supportedLangs = ['en', 'es', 'de'], children }) => {
  // Detect browser language and use if supported, else fallback to defaultLang
  const detectLang = () => {
    const browserLang = (navigator.languages && navigator.languages[0]) || navigator.language || defaultLang;
    const shortLang = browserLang.split('-')[0];
    return supportedLangs.includes(shortLang) ? shortLang : defaultLang;
  };

  const [i18n] = useState(() => new I18n(defaultLang));
  const [lang, setLang] = useState(detectLang());
  const [ready, setReady] = useState(false);
  const [t, setT] = useState(() => (key) => key);

  useEffect(() => {
    let mounted = true;
    setReady(false);
    i18n.load(lang).then(() => {
      if (mounted) {
        setT(() => i18n.t.bind(i18n));
        setReady(true);
      }
    });
    return () => { mounted = false; };
  }, [lang, i18n]);

  return (
    <I18nContext.Provider value={{ t, lang, setLang, ready }}>
      {ready ? children : null}
    </I18nContext.Provider>
  );
};

I18nProvider.propTypes = {
  defaultLang: PropTypes.string,
  supportedLangs: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
};

export const useI18n = () => useContext(I18nContext);
